import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from 'recharts';
import { fmtMoney, fmtHours } from './format';
import { c, chart } from '../../theme';

// ── Ranked overtime cost, at whichever grain the table below is showing ──────
//
// A single series: length is the message, so every bar is the same hue and no
// legend is needed -- the heading says what is plotted. Colour is NOT assigned
// by rank, which would repaint the survivors every time a filter changes the row
// set and read as movement that did not happen.
//
// Deliberately not a pie. Two of the five groupings this follows have 42 and 336
// classes; past about seven, adjacent slices blur into each other and the form
// stops carrying information. Class has two values, where a pie is just two
// numbers drawn as a circle. A ranked bar is the honest form at every grain the
// switch offers, and the table underneath is the exact-value view.
//
// Long dimensions fold to a top ten plus an explicit "Other" bar rather than
// scrolling to 336 rows -- and "Other" is drawn, never dropped, so the bars
// still sum to the total on screen.

const TOP_N = 10;

export default function RoleRankChart({ rows = [], unmapped = null, heading = 'Role' }) {
  const all = [
    ...rows.map((r) => ({ key: r.key, amount: Number(r.ot_amount), hours: Number(r.ot_hours),
                          share: r.share_amount, kind: 'row' })),
    ...(unmapped ? [{ key: 'Not in the mapping', amount: Number(unmapped.ot_amount),
                      hours: Number(unmapped.ot_hours), share: unmapped.share_amount,
                      kind: 'unmapped' }] : []),
  ];
  if (!all.length) return null;

  const head = all.slice(0, TOP_N);
  const tail = all.slice(TOP_N);
  const data = tail.length
    ? [...head, {
        key: `Other (${tail.length})`,
        amount: tail.reduce((a, r) => a + r.amount, 0),
        hours:  tail.reduce((a, r) => a + r.hours, 0),
        share:  Math.round(tail.reduce((a, r) => a + (r.share || 0), 0) * 10) / 10,
        kind: 'other',
      }]
    : head;

  const fill = (kind) =>
    kind === 'unmapped' ? c.warning : kind === 'other' ? chart.other : chart.recessive;

  const label = (k) => (k.length > 28 ? k.slice(0, 27) + '…' : k);
  const height = Math.max(180, data.length * 30 + 44);

  return (
    <div style={s.card}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 8 }}>
          <CartesianGrid horizontal={false} stroke={chart.grid} />
          <XAxis type="number" tickFormatter={fmtMoney}
                 tick={{ fontSize: 11, fill: chart.axisText }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="key" width={188} interval={0}
                 tickFormatter={label}
                 tick={{ fontSize: 11, fill: chart.axisTextStrong }} axisLine={false} tickLine={false} />
          <Tooltip
            cursor={{ fill: 'rgba(27,42,74,0.05)' }}
            formatter={(v, _n, p) => [
              `${fmtMoney(v)} · ${fmtHours(p?.payload?.hours)} · ${p?.payload?.share}%`,
              heading,
            ]}
            labelFormatter={(l) => l}
            contentStyle={{ fontSize: '12px', borderRadius: '8px', border: `1px solid ${chart.tooltipBorder}` }}
          />
          {/* 24px cap: a bar should never fill its band -- the leftover is the air
              that keeps a dense chart readable. */}
          <Bar dataKey="amount" radius={[0, 4, 4, 0]} maxBarSize={24}>
            {data.map((r) => <Cell key={r.key} fill={fill(r.kind)} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const s = {
  card: { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '12px',
          padding: '14px 10px 6px', marginBottom: '10px' },
};
